/**
 * FleetHealthHeatmap - Colored dot matrix showing inspection scores by vehicle x week
 *
 * Rows = vehicles (sorted worst-current-grade first)
 * Columns = last 12 weeks (most recent right)
 * Cells = colored circles with grade letter tooltip
 * Empty weeks = gray dot with "—"
 */
import React, { useState, useMemo } from 'react';
import { Loader2, Activity, Info } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../api.js';
import { inspectionAPI } from '../../api/maintenance.js';
import Pagination, { usePagination } from '../common/Pagination.jsx';

// Grade scale (credit-rating style)
const GRADES = [
  { min: 130, grade: 'AA', color: '#22c55e' },
  { min: 110, grade: 'A',  color: '#84cc16' },
  { min: 85,  grade: 'BBB', color: '#eab308' },
  { min: 60,  grade: 'BB', color: '#f97316' },
  { min: 30,  grade: 'B',  color: '#ef4444' },
  { min: 0,   grade: 'D',  color: '#7f1d1d' },
];

function scoreToGrade(score) {
  for (const g of GRADES) {
    if (score >= g.min) return g;
  }
  return GRADES[GRADES.length - 1];
}

function getIsoWeek(date) {
  const d = new Date(date);
  const jan1 = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
}

function GradeCell({ cell, onClick }) {
  const { t } = useTranslation();
  if (!cell) {
    return (
      <div
        className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[8px] text-gray-400 cursor-default"
        title={t('reports.health.noInspection')}
      >
        —
      </div>
    );
  }

  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-400 transition-shadow"
      style={{ backgroundColor: cell.color, color: 'white' }}
      title={`${cell.grade} (${cell.score}/150)`}
      onClick={onClick}
    >
      {cell.grade.length <= 2 ? cell.grade : cell.grade[0]}
    </div>
  );
}

function LegendBar() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
      <span className="font-medium">{t('reports.health.legend')}:</span>
      {GRADES.map(g => (
        <div key={g.grade} className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: g.color }} />
          <span>{g.grade} ({g.min}+)</span>
        </div>
      ))}
      <div className="flex items-center gap-1 ml-2">
        <div className="w-4 h-4 rounded-full bg-gray-200" />
        <span>{t('reports.health.none')}</span>
      </div>
    </div>
  );
}

export default function FleetHealthHeatmap() {
  const { t } = useTranslation();
  const [selectedCell, setSelectedCell] = useState(null);

  // Fetch vehicles
  const { data: vehiclesRaw = [] } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const res = await api.vehicle.getAll();
      return res.data || res || [];
    },
  });

  // Fetch inspections
  const { data: inspectionsRaw = [], isLoading, error } = useQuery({
    queryKey: ['inspections-heatmap'],
    queryFn: async () => {
      const res = await inspectionAPI.getAll();
      return res.data || res || [];
    },
    staleTime: 120000,
  });

  const vehicles = Array.isArray(vehiclesRaw) ? vehiclesRaw.filter(v => v.status !== 'retired') : [];
  const inspections = Array.isArray(inspectionsRaw) ? inspectionsRaw : [];

  // Build heatmap data
  const heatmapData = useMemo(() => {
    // Vehicle map
    const vehicleMap = {};
    for (const v of vehicles) vehicleMap[v.id] = v;

    // Generate last 12 weeks
    const now = new Date();
    const weeks = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - (i * 7));
      const weekNum = getIsoWeek(d);
      weeks.push({ year: d.getFullYear(), week: weekNum, label: t('reports.health.weekShort', { week: weekNum }) });
    }

    // Group inspections by vehicle + week
    const matrix = {};
    const vehicleLatestScore = {};

    for (const insp of inspections) {
      const vid = insp.vehicle_id;
      if (!vehicleMap[vid]) continue;

      const inspDate = new Date(insp.inspection_date);
      const weekNum = getIsoWeek(inspDate);
      const weekKey = `${inspDate.getFullYear()}-${weekNum}`;

      let score;
      if (insp.overall_score !== null && insp.overall_score !== undefined) {
        score = insp.overall_score;
      } else {
        // DVIR: convert pass/fail to 150-point scale
        if (insp.status === 'passed') {
          score = 150;
        } else if (insp.status === 'failed') {
          const issueCount = insp.issues_found || 1;
          score = Math.max(0, Math.round(150 * (1 - issueCount * 0.15)));
        } else {
          score = 100;
        }
      }

      if (!matrix[vid]) matrix[vid] = {};
      // Keep worst score if multiple inspections in same week
      if (!matrix[vid][weekKey] || score < matrix[vid][weekKey].score) {
        const gradeInfo = scoreToGrade(score);
        matrix[vid][weekKey] = { score, grade: gradeInfo.grade, color: gradeInfo.color };
      }

      if (!vehicleLatestScore[vid] || score < vehicleLatestScore[vid]) {
        vehicleLatestScore[vid] = score;
      }
    }

    // Build rows sorted by worst-current-grade first
    const inspectedIds = Object.keys(matrix).map(Number);
    inspectedIds.sort((a, b) => (vehicleLatestScore[a] || 999) - (vehicleLatestScore[b] || 999));

    const rows = inspectedIds.map(vid => {
      const v = vehicleMap[vid];
      return {
        vehicle_id: vid,
        license_plate: v?.license_plate || '',
        make: v?.make || '',
        model: v?.model || '',
        cells: weeks.map(w => matrix[vid]?.[`${w.year}-${w.week}`] || null),
      };
    });

    // Add uninspected vehicles at the end
    const inspectedSet = new Set(inspectedIds);
    const uninspected = vehicles
      .filter(v => !inspectedSet.has(v.id))
      .map(v => ({
        vehicle_id: v.id,
        license_plate: v.license_plate || '',
        make: v.make || '',
        model: v.model || '',
        cells: weeks.map(() => null),
      }));

    return { weeks: weeks.map(w => w.label), rows: [...rows, ...uninspected] };
  }, [vehicles, inspections, t]);

  const { currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, paginateArray } = usePagination(heatmapData.rows.length, 1, 25);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('reports.health.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 text-gray-500">
        <Activity className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">{t('common.states.loadError')}</p>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  const { weeks, rows } = heatmapData;

  if (rows.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <Activity className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">{t('reports.health.emptyTitle')}</p>
        <p className="text-sm mt-1">
          {t('reports.health.emptyHint')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-800">{t('reports.health.title')}</h3>
          <span className="text-sm text-gray-500">({t('reports.health.scope', { count: rows.length, weeks: 12 })})</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Info className="w-3 h-3" />
          {t('reports.health.clickHint')}
        </div>
      </div>

      {/* Legend */}
      <LegendBar />

      {/* Heatmap Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[180px]">
                {t('reports.health.vehicle')}
              </th>
              {weeks.map((w, i) => (
                <th key={i} className="px-1 py-2 text-center text-xs font-medium text-gray-400 min-w-[36px]">
                  {w}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginateArray(rows).map((row) => (
              <tr key={row.vehicle_id} className="hover:bg-blue-50/30 transition-colors">
                <td className="px-3 py-1.5 text-sm sticky left-0 bg-white z-10">
                  <div className="font-medium text-gray-800 truncate max-w-[170px]">
                    {row.license_plate}
                  </div>
                  <div className="text-xs text-gray-400 truncate max-w-[170px]">
                    {row.make} {row.model}
                  </div>
                </td>
                {(row.cells || []).map((cell, i) => (
                  <td key={i} className="px-1 py-1.5 text-center">
                    <div className="flex justify-center">
                      <GradeCell
                        cell={cell}
                        onClick={() => cell && setSelectedCell({ ...cell, vehicle: row, weekLabel: weeks[i] })}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={rows.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200"
      />

      {/* Detail popup */}
      {selectedCell && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <span className="font-medium text-blue-900">
              {selectedCell.vehicle.license_plate} — {selectedCell.weekLabel}
            </span>
            <span className="ml-3 text-blue-700">
              {t('reports.health.score')}: {selectedCell.score}/150 — {t('reports.health.grade')}: {selectedCell.grade}
            </span>
          </div>
          <button
            onClick={() => setSelectedCell(null)}
            className="text-blue-400 hover:text-blue-600 text-sm"
          >
            {t('common.actions.close')}
          </button>
        </div>
      )}
    </div>
  );
}
